import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, ChevronLeft, ChevronRight, Rocket } from 'lucide-react';
import { Button } from '../ui/button';
import { formatDateOnly } from '../../lib/dates';
import {
  addDays,
  addMonths,
  parseMonthKey,
  SR_PANEL,
  toDateOnly,
  toMonthKey,
  weekdayHeadings,
} from '../../lib/service-request-views';
import { cn } from '../../lib/utils';
import type { LaunchStep } from './use-launch-steps';

export type CalendarInitiative = {
  id: string;
  key: string;
  title: string;
  due_date: string | null;
  to: string;
};

type EventKind = 'launch' | 'step' | 'live';

type CalendarEvent = {
  id: string;
  date: string;
  kind: EventKind;
  key: string;
  title: string;
  initiative: string;
  to: string;
};

const PER_DAY_VISIBLE = 3;
const GRID_DAYS = 42;

const TONE: Record<EventKind, string> = {
  launch: 'bg-blue-100 text-blue-900 dark:bg-blue-950 dark:text-blue-100',
  step: 'bg-violet-100 text-violet-900 dark:bg-violet-950 dark:text-violet-100',
  live: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-100',
};

const LABEL: Record<EventKind, string> = {
  launch: 'Initiative launch',
  step: 'Step go-live',
  live: 'Step live',
};

function gridStart(month: Date): Date {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  return addDays(first, -((first.getDay() + 6) % 7));
}

function buildEvents(initiatives: CalendarInitiative[], steps: Map<string, LaunchStep[]>): CalendarEvent[] {
  const events: CalendarEvent[] = [];
  for (const initiative of initiatives) {
    if (initiative.due_date) {
      events.push({
        id: `launch-${initiative.id}`,
        date: initiative.due_date,
        kind: 'launch',
        key: initiative.key,
        title: initiative.title,
        initiative: initiative.title,
        to: initiative.to,
      });
    }
    for (const step of steps.get(initiative.id) ?? []) {
      if (!step.due_date) continue;
      events.push({
        id: `step-${step.id}`,
        date: step.due_date,
        kind: step.live_at ? 'live' : 'step',
        key: step.key,
        title: step.title,
        initiative: initiative.title,
        to: step.to,
      });
    }
  }
  return events.sort((a, b) => a.date.localeCompare(b.date) || (a.kind === 'launch' ? -1 : b.kind === 'launch' ? 1 : 0));
}

function EventChip({ event }: { event: CalendarEvent }) {
  return (
    <Link
      to={event.to}
      title={`${LABEL[event.kind]} · ${event.key} · ${event.title}${event.kind === 'launch' ? '' : ` · ${event.initiative}`}`}
      className={cn(
        'flex items-center gap-1 truncate rounded px-1 py-0.5 text-[11px] leading-tight transition-opacity hover:opacity-80 hover:no-underline',
        TONE[event.kind],
      )}
    >
      {event.kind === 'launch' ? <Rocket className="h-3 w-3 shrink-0" aria-hidden /> : null}
      {event.kind === 'live' ? <CheckCircle2 className="h-3 w-3 shrink-0" aria-hidden /> : null}
      <span className="truncate">{event.title}</span>
    </Link>
  );
}

function EventRow({ event }: { event: CalendarEvent }) {
  return (
    <Link
      to={event.to}
      className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm transition-colors hover:bg-accent/60 hover:no-underline"
    >
      <span className={cn('inline-flex whitespace-nowrap rounded-md px-1.5 py-0.5 text-[11px] font-medium', TONE[event.kind])}>
        {LABEL[event.kind]}
      </span>
      <span className="font-mono text-[11px] font-semibold tabular-nums text-primary">{event.key}</span>
      <span className="min-w-0 flex-1 truncate font-medium">{event.title}</span>
      {event.kind === 'launch' ? null : (
        <span className="hidden truncate text-xs text-muted-foreground md:inline">{event.initiative}</span>
      )}
    </Link>
  );
}

function Legend({ className }: { className?: string }) {
  return (
    <ul className={cn('flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground', className)}>
      {(['launch', 'step', 'live'] as const).map((kind) => (
        <li key={kind} className="inline-flex items-center gap-1.5">
          <span className={cn('h-2.5 w-2.5 rounded-sm', TONE[kind])} aria-hidden />
          {LABEL[kind]}
        </li>
      ))}
    </ul>
  );
}

/** Month calendar of initiative launches and the go-live dates of their launch steps. */
export default function InitiativeCalendar({
  initiatives,
  steps,
}: {
  initiatives: CalendarInitiative[];
  steps: Map<string, LaunchStep[]>;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const today = useMemo(() => new Date(), []);
  const todayKey = toDateOnly(today);
  const thisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const month = parseMonthKey(searchParams.get('month')) ?? thisMonth;
  const [selected, setSelected] = useState<string | null>(null);

  const events = useMemo(() => buildEvents(initiatives, steps), [initiatives, steps]);
  const byDate = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const event of events) {
      const list = map.get(event.date) ?? [];
      list.push(event);
      map.set(event.date, list);
    }
    return map;
  }, [events]);

  const start = gridStart(month);
  const days = Array.from({ length: GRID_DAYS }, (_, index) => {
    const day = addDays(start, index);
    return { date: toDateOnly(day), inMonth: day.getMonth() === month.getMonth() };
  });
  const headings = useMemo(() => weekdayHeadings(), []);
  const upcomingLimit = toDateOnly(addDays(today, 14));
  const upcoming = events.filter((event) => event.date >= todayKey && event.date <= upcomingLimit);
  const monthLabel = month.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  const goTo = (next: Date) => {
    const params = new URLSearchParams(searchParams);
    if (toMonthKey(next) === toMonthKey(thisMonth)) params.delete('month');
    else params.set('month', toMonthKey(next));
    setSearchParams(params, { replace: true });
    setSelected(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex items-center gap-1">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8"
            aria-label="Previous month"
            onClick={() => goTo(addMonths(month, -1))}
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8"
            aria-label="Next month"
            onClick={() => goTo(addMonths(month, 1))}
          >
            <ChevronRight className="h-4 w-4" aria-hidden />
          </Button>
        </div>
        <h2 className="text-base font-semibold">{monthLabel}</h2>
        <Button type="button" variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => goTo(thisMonth)}>
          Today
        </Button>
        <Legend className="ml-auto" />
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_19rem]">
        <div className={cn(SR_PANEL, 'overflow-hidden')}>
          <div className="grid grid-cols-7 border-b bg-muted/40">
            {headings.map((heading) => (
              <div key={heading} className="px-1.5 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {heading}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 [&>*:nth-child(7n)]:border-r-0 [&>*:nth-last-child(-n+7)]:border-b-0">
            {days.map((day) => {
              const items = byDate.get(day.date) ?? [];
              const hidden = Math.max(0, items.length - PER_DAY_VISIBLE);
              const dayNumber = Number(day.date.slice(8, 10));
              const isToday = day.date === todayKey;
              return (
                <div
                  key={day.date}
                  className={cn(
                    'flex min-h-24 flex-col gap-1 border-b border-r p-1.5',
                    !day.inMonth && 'bg-muted/30',
                    selected === day.date && 'bg-accent/50 ring-1 ring-inset ring-primary/40',
                  )}
                  aria-current={isToday ? 'date' : undefined}
                >
                  <button
                    type="button"
                    onClick={() => setSelected((current) => (current === day.date ? null : day.date))}
                    className={cn(
                      'w-fit rounded px-1 font-mono text-xs tabular-nums transition-colors hover:bg-accent',
                      isToday && 'bg-primary font-semibold text-primary-foreground hover:bg-primary/90',
                      !isToday && (day.inMonth ? 'text-foreground' : 'text-muted-foreground'),
                    )}
                    aria-label={`${formatDateOnly(day.date) ?? day.date}${items.length ? `, ${items.length} dates` : ', no dates'}`}
                  >
                    {dayNumber === 1 ? `${dayNumber} ${new Date(`${day.date}T00:00:00`).toLocaleDateString(undefined, { month: 'short' })}` : dayNumber}
                  </button>
                  <div className="space-y-0.5">
                    {items.slice(0, PER_DAY_VISIBLE).map((event) => (
                      <EventChip key={event.id} event={event} />
                    ))}
                    {hidden > 0 ? (
                      <button
                        type="button"
                        onClick={() => setSelected(day.date)}
                        className="w-full rounded px-1 text-left text-[11px] text-muted-foreground hover:bg-accent hover:text-foreground"
                      >
                        +{hidden} more
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <aside className={cn(SR_PANEL, 'p-4')} aria-label="Coming up">
          <h2 className="text-sm font-semibold">Next two weeks</h2>
          {upcoming.length === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground">No launches or go-live dates in the next 14 days.</p>
          ) : (
            <ul className="mt-3 space-y-2.5">
              {upcoming.map((event) => (
                <li key={event.id}>
                  <Link to={event.to} className="block hover:no-underline">
                    <span className="flex items-center gap-1.5">
                      <span className={cn('h-2.5 w-2.5 shrink-0 rounded-sm', TONE[event.kind])} aria-hidden />
                      <span className="min-w-0 truncate text-sm font-medium">{event.title}</span>
                    </span>
                    <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                      {event.date === todayKey ? 'Today' : formatDateOnly(event.date) ?? event.date}
                      {event.kind === 'launch' ? ' · Initiative launch' : ` · ${event.initiative}`}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </aside>
      </div>

      {selected ? (
        <section className={cn(SR_PANEL, 'overflow-hidden')} aria-label={`Dates on ${formatDateOnly(selected) ?? selected}`}>
          <header className="flex items-baseline gap-2 border-b px-3 py-2">
            <h2 className="text-sm font-semibold">{formatDateOnly(selected) ?? selected}</h2>
            <span className="ml-auto font-mono text-xs tabular-nums text-muted-foreground">
              {(byDate.get(selected) ?? []).length}
            </span>
          </header>
          {(byDate.get(selected) ?? []).length === 0 ? (
            <p className="px-3 py-3 text-sm text-muted-foreground">Nothing scheduled on this day.</p>
          ) : (
            <ul className="divide-y">
              {(byDate.get(selected) ?? []).map((event) => (
                <li key={event.id}>
                  <EventRow event={event} />
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}
    </div>
  );
}
